import { timesOptions } from '@Utils/DateUtils';
import _, { isEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import {
  EventNodes,
  JourneyObjectModelSteps,
  NodesInbox,
  StepsAvailableForChannel,
} from './static';

// Hook
export function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

export function findMaxId(elements) {
  const max =
    elements.length &&
    elements.reduce((prev, current) => {
      return Number(prev.id) > Number(current.id) ? prev : current;
    });
  return Number(max?.id || 0);
}

export function filterJourneyVariableSteps(steps, currentStepId) {
  try {
    const getCallApiResponse = (step) => {
      let response = step?.ui?.response;

      if (_.isString(response)) {
        try {
          response = JSON.parse(response);
        } catch (e) {
          return response;
        }
      }

      return response;
    };

    const hasCallApiResponseError = (step) => {
      const response = getCallApiResponse(step);

      if (isEmpty(response)) return true;
      if (response?.errorMessage) return true;

      const statusCode = parseInt(response?.statusCode, 10);
      if (!Number.isNaN(statusCode) && statusCode >= 400) return true;

      return false;
    };

    return steps.filter((step) => {
      if (step.type === StepsAvailableForChannel[0] && !step?.event)
        return false;
      if (
        step.type === StepsAvailableForChannel[1] &&
        hasCallApiResponseError(step)
      )
        return false;
      if (step.type === StepsAvailableForChannel[2] && !step?.event)
        return false;
      if (step.type === StepsAvailableForChannel[3] && !step?.businessEvent)
        return false;
      if (
        StepsAvailableForChannel.includes(step.type) &&
        Number(step.id) !== Number(currentStepId)
      )
        return true;
    });
  } catch (e) {
    // TODO GrayLog
    console.log('error->>', e);
    return [];
  }
}

export const GetNodePortsByType = (type) => {
  try {
    if (type === 'PLACE_HOLDER') return [];

    const node = NodesInbox().find((m) => m.data?.key === type);

    if (node) {
      const events = _.get(node, 'data.events', []);
      // const events = node.data.events;
      if (events.length) {
        return events;
      } else {
        return [
          {
            name: 'on',
          },
        ];
      }
    }
    return [];
  } catch (e) {
    // TODO GrayLog
    console.log('error->>', e);
    return [];
  }
};

export const isUsedOnWaitForDate = (journey, nodeId) => {
  const allWaitForDate = journey.steps.filter(
    (step) => step.type === JourneyObjectModelSteps.WAIT_FOR_DATE.type
  );
  if (allWaitForDate.length) {
    // CHECK STRAIGHT
    const stepsThatUsed = allWaitForDate.filter((wd) => {
      return wd.stepId + '' === nodeId + '';
    });
    // CHECK NOT STRAIGHT

    return stepsThatUsed;
  }
  return [];
};

export const isJourneyVariableNode = (journey, nodeId) => {
  const allJourneyVariableNodes = journey.steps.filter((step) =>
    StepsAvailableForChannel.includes(step.type)
  );
  if (allJourneyVariableNodes.length) {
    const isFound = allJourneyVariableNodes.find((node) => {
      return node.id + '' === nodeId + '';
    });
    return !!isFound;
  }
  return false;
};

export const dfsSearchToFindPath = (
  startNode_,
  targetWfNode_,
  journey,
  detect = false
) => {
  try {
    const isEventNode = EventNodes.find((en) => en === startNode_.type);
    // 100% Must be OCC_Event Or WF_Event node
    let parentNode;
    if (isEventNode) {
      parentNode = startNode_;
    } else {
      parentNode = journey.steps.find(
        (step) => step.id + '' === targetWfNode_.stepId + ''
      );
    }

    if (!parentNode) return;
    if (!EventNodes.find((en) => en === parentNode.type)) {
      console.log('error-----', parentNode);
      // TODO: Add to GrayLog (it's not possible)
      return;
    }

    const dfsSearch = (eventNode, checkConnection = false) => {
      let targetNodes = [];
      const eventNodePorts = GetNodePortsByType(eventNode.type);

      eventNodePorts.forEach((port) => {
        const targets = eventNode[port.name] || [];
        if (targets.length) {
          targetNodes.push(...targets);
        }
      });

      if (targetNodes.length) {
        const children = Array.from(new Set(targetNodes));
        let isExist;
        if (detect) {
          isExist = children.find(
            (child) => child + '' === targetWfNode_.id + ''
          );
        } else if (!checkConnection) {
          isExist = children.find((child) => child + '' === startNode_.id + '');
        }
        if (checkConnection) {
          const hasConnection =
            checkConnection &&
            children.find((child) => child + '' === targetWfNode_.id + '');
          if (hasConnection) return true;
        }

        if (isExist) {
          // check connection with WF_date node
          if (detect) {
            return true;
          } else {
            const isInPath = dfsSearch(startNode_, true);
            if (isInPath || isInPath === undefined) {
              return true;
            }
          }
          return false;
        } else {
          // recursive find
          return children.find((child) => {
            const nextChild = journey.steps.find(
              (stp) => stp.id + '' === child + ''
            );
            return dfsSearch(nextChild);
          });
        }
      } else {
        return false;
      }
    };
    return dfsSearch(parentNode);
  } catch (e) {
    // TODO GrayLog
    console.log('error->>', e);
    return null;
  }
};

export const dfsSearchPrime = (
  startNode_,
  targetWfNode_,
  journey,
  detect = false
) => {
  try {
    const isEventNode = EventNodes.find((en) => en === startNode_.type);

    const parentNode = startNode_;

    const dfsSearch = (eventNode, targetNode) => {
      const targetNode_ = targetNode || targetWfNode_; // static
      let targetNodes = [];
      const eventNodePorts = GetNodePortsByType(eventNode.type);

      eventNodePorts.forEach((port) => {
        const targets = eventNode[port.name] || [];
        if (targets.length) {
          targetNodes.push(...targets);
        }
      });

      if (targetNodes.length) {
        const children = Array.from(new Set(targetNodes));
        let isExist;
        isExist = children.find((child) => child + '' === targetNode_.id + '');
        if (isExist) {
          return true;
        } else {
          // recursive find
          return children.find((child) => {
            const nextChild = journey.steps.find(
              (stp) => stp.id + '' === child + ''
            );
            return dfsSearch(nextChild, targetNode_);
          });
        }
      } else {
        return false;
      }
    };
    const hasConnection = dfsSearch(parentNode);

    // stepId this node has connection by startNode ??
    if (hasConnection) {
      if (!isEventNode) {
        const topNode = journey.steps.find(
          (item) => item.id === targetWfNode_.stepId
        );
        return dfsSearch(topNode, startNode_);
      } else {
        if (detect) {
          return true;
        }
        return startNode_.id + '' === targetWfNode_.stepId + '';
      }
    } else {
      return false;
    }
  } catch (e) {
    // TODO GrayLog
    console.log('error->>', e);
    return null;
  }
};

export const tryParseJSONObject = (jsonString) => {
  try {
    var o = JSON.parse(jsonString);
    // Handle non-exception-throwing cases:
    // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
    // but... JSON.parse(null) returns null, and typeof null === "object",
    // so we must check for that, too. Thankfully, null is falsey, so this suffices:
    if (o && typeof o === 'object') {
      return o;
    }
  } catch (e) {
    // TODO GrayLog
    console.log('error->>', e);
    return false;
  }

  return false;
};

export const calcMinuteTime = (time, type) => {
  try {
    if (time && type) {
      const timeType = timesOptions().find((t) => t.id === type);
      if (timeType) {
        const z = timeType?.min;
        return Number(time * z);
      }
    }
    return 0;
  } catch (error) {
    // TODO GrayLog
    console.log('error->>>', error);
    return 0;
  }
};

export const ORBIT_MISSION_START_EVENT_NAME = '[INT]_orbit_mission_start';
export const ORBIT_CALL_UI_KEY = 'ORBIT_CALL';

export function findOrbitMissionEventOccurrence(steps, events) {
  if (!Array.isArray(steps) || !Array.isArray(events)) {
    return null;
  }
  return (
    steps.find((step) => {
      if (step?.type !== 'EVENT_OCCURRENCE' || !step.event) {
        return false;
      }
      const event = events.find(
        (e) => e.id === step.event || e.id === Number(step.event)
      );
      return event?.name === ORBIT_MISSION_START_EVENT_NAME;
    }) || null
  );
}

export function hasOrbitMissionEventOccurrence(steps, events) {
  return !!findOrbitMissionEventOccurrence(steps, events);
}

export function buildOrbitCallOmtiTemplate(eventStepId) {
  return `{{journey["state-${eventStepId}"].custom["omti"]}}`;
}

/**
 * Persist Call API body as a raw JSON-looking string (same as the Call API textarea).
 * Do not JSON.stringify the object — that would escape quotes inside the omti template
 * and the journey engine would not interpolate {{journey["state-N"].custom["omti"]}}.
 */
export function buildOrbitCallBody(osi, eventStepId) {
  const omtiTemplate = buildOrbitCallOmtiTemplate(eventStepId);
  return `{"omti":"${omtiTemplate}","osi":${JSON.stringify(osi ?? '')}}`;
}

export function parseOrbitCallOsi(body) {
  if (!body) {
    return '';
  }
  if (typeof body !== 'string') {
    return body?.osi || '';
  }
  try {
    const parsed = JSON.parse(body);
    return parsed?.osi || '';
  } catch (e) {
    // Unescaped journey templates make the body invalid JSON until interpolation.
    const match = body.match(/"osi"\s*:\s*("(?:\\.|[^"\\])*")/);
    if (match) {
      try {
        return JSON.parse(match[1]);
      } catch (err) {
        return '';
      }
    }
    return '';
  }
}

export function getOrbitCallHeaders() {
  return [
    { key: 'X-Orbit-Tracker', value: 'ORBIT_API_KEY' },
    { key: 'Content-Type', value: 'application/json' },
  ];
}

const DEFAULT_ORBIT_TRACKER_URL = 'https://orbit.smartech.ir/webhooks/tracker';

export function getOrbitTrackerUrl() {
  const runtimeConfig =
    typeof window !== 'undefined' ? window.__ENV_CONFIG__ || {} : {};
  const configured = runtimeConfig.ORBIT_TRACKER_URL;
  if (typeof configured === 'string' && configured.trim()) {
    return configured.trim();
  }
  return DEFAULT_ORBIT_TRACKER_URL;
}

export function buildOrbitCallApiStepDefaults() {
  return {
    ...JourneyObjectModelSteps.CALL_API,
    type: 'CALL_API',
    method: 'POST',
    url: getOrbitTrackerUrl(),
    headers: getOrbitCallHeaders(),
    body: undefined,
  };
}

export function isOrbitCallStep(step) {
  if (!step) {
    return false;
  }
  if (step?.ui?.key === ORBIT_CALL_UI_KEY || step?.ui?.isOrbitCall === true) {
    return true;
  }
  // Fallback: Orbit Call is persisted as CALL_API; detect via dedicated header
  // (covers duplicate/reload cases where ui.key was lost)
  if (step?.type === 'CALL_API' && Array.isArray(step.headers)) {
    return step.headers.some(
      (h) => h?.key === 'X-Orbit-Tracker' && h?.value === 'ORBIT_API_KEY'
    );
  }
  return false;
}

/**
 * Build a CALL_API step payload for a new/replaced Orbit Call canvas node.
 */
export function buildOrbitCallStepForCache({ id, ui, dNode = {} }) {
  const orbitDefaults = buildOrbitCallApiStepDefaults();
  return Object.assign({}, orbitDefaults, dNode, {
    id: Number(id),
    type: 'CALL_API',
    method: 'POST',
    url: orbitDefaults.url,
    headers: orbitDefaults.headers,
    onSuccess: dNode?.onSuccess || [],
    onFailure: dNode?.onFailure || [],
    ui: Object.assign({}, ui, { key: ORBIT_CALL_UI_KEY }),
  });
}

function inferOrbitEventStep(step, steps) {
  const match = String(step?.body || '').match(/state-(\d+)/);
  if (match) {
    const id = Number(match[1]);
    return steps?.find((s) => Number(s.id) === id) || { id };
  }
  return steps?.find((s) => s?.type === 'EVENT_OCCURRENCE') || null;
}

/**
 * Re-resolve the orbit EVENT_OCCURRENCE step id and rebuild the Call API body.
 * Returns the same step reference when nothing changed; otherwise a new step object.
 */
export function refreshOrbitCallStepBody(step, steps, events) {
  if (!isOrbitCallStep(step) && step?.ui?.key !== ORBIT_CALL_UI_KEY) {
    return step;
  }

  const orbitEventStep =
    findOrbitMissionEventOccurrence(steps, events) ||
    inferOrbitEventStep(step, steps);
  if (!orbitEventStep) {
    return step;
  }

  const osi = parseOrbitCallOsi(step.body);
  if (!osi) {
    return step;
  }

  const nextBody = buildOrbitCallBody(osi, orbitEventStep.id);
  if (step.body === nextBody) {
    return step;
  }

  return Object.assign({}, step, {
    type: 'CALL_API',
    method: 'POST',
    url: getOrbitTrackerUrl(),
    headers: getOrbitCallHeaders(),
    body: nextBody,
    ui: Object.assign({}, step.ui, { key: ORBIT_CALL_UI_KEY }),
  });
}

/**
 * Rebuild Orbit Call bodies on a steps copy right before updateSubmit/launch.
 * Converts leftover JSON.stringify-escaped templates without waiting for canvas refresh.
 */
export function prepareOrbitCallStepsForApi(steps, events) {
  if (!Array.isArray(steps)) {
    return steps;
  }
  return steps.map((step) => refreshOrbitCallStepBody(step, steps, events));
}

/**
 * Refresh all Orbit Call steps in a journey. Returns steps whose body was updated.
 */
export function refreshAllOrbitCallSteps(steps, events) {
  if (!Array.isArray(steps)) {
    return [];
  }

  const updated = [];
  steps.forEach((step, index) => {
    if (!isOrbitCallStep(step) && step?.ui?.key !== ORBIT_CALL_UI_KEY) {
      return;
    }
    const refreshed = refreshOrbitCallStepBody(step, steps, events);
    if (refreshed !== step) {
      steps[index] = refreshed;
      updated.push(refreshed);
    }
  });
  return updated;
}
